/**
 * ============================================================================
 * UI STORE - ZUSTAND
 * ============================================================================
 * 
 * Global UI state management (modals, toasts, theme, etc.)
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const useUIStore = create(
  persist(
    (set, get) => ({
      // Theme (default: dark everywhere)
      theme: 'dark',
      effectiveTheme: 'dark',

      // Modals
      modals: {
        // Modal states
        login: false,
        signup: false,
        transaction: false,
        payment: false,
        dispute: false,
        withdrawal: false,
        settings: false,
        profile: false,
        kyc: false,
        deleteAccount: false,
        // Generic modal
        generic: {
          open: false,
          component: null,
          props: {},
        },
      },

      // Toasts/Notifications
      toasts: [],

      // Sidebar/Drawer
      sidebar: {
        open: false,
        mobile: false,
      },

      // Loading states
      globalLoading: false,
      loadingMessage: null,

      // Drawer states
      drawer: {
        open: false,
        content: null,
        position: 'right', // 'left', 'right', 'top', 'bottom'
      },

      // Actions - Theme
      setTheme: (theme) => {
        const effective =
          theme === 'system'
            ? window.matchMedia('(prefers-color-scheme: dark)').matches
              ? 'dark'
              : 'light'
            : theme;
        set({ theme, effectiveTheme: effective });
        document.documentElement.setAttribute('data-theme', effective);
      },

      setEffectiveTheme: (theme) => {
        set({ effectiveTheme: theme });
        document.documentElement.setAttribute('data-theme', theme);
      },

      // Actions - Modals
      openModal: (modalName, props = {}) => {
        set((state) => ({
          modals: {
            ...state.modals,
            [modalName]: true,
            ...(props && { [`${modalName}Props`]: props }),
          },
        }));
      },

      closeModal: (modalName) => {
        set((state) => ({
          modals: {
            ...state.modals,
            [modalName]: false,
            [`${modalName}Props`]: {},
          },
        }));
      },

      closeAllModals: () => {
        set((state) => ({
          modals: Object.keys(state.modals).reduce((acc, key) => {
            if (key === 'generic') {
              acc[key] = { open: false, component: null, props: {} };
            } else {
              acc[key] = false;
            }
            return acc;
          }, {}),
        }));
      },

      openGenericModal: (component, props = {}) => {
        set((state) => ({
          modals: {
            ...state.modals,
            generic: {
              open: true,
              component,
              props,
            },
          },
        }));
      },

      closeGenericModal: () => {
        set((state) => ({
          modals: {
            ...state.modals,
            generic: {
              open: false,
              component: null,
              props: {},
            },
          },
        }));
      },

      // Actions - Toasts
      addToast: (toast) => {
        const id = toast.id || `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        const newToast = {
          id,
          type: toast.type || 'info', // 'success', 'error', 'warning', 'info'
          message: toast.message,
          duration: toast.duration || 5000,
          action: toast.action,
          ...toast,
        };

        set((state) => ({
          toasts: [...state.toasts, newToast],
        }));

        // Auto remove after duration
        if (newToast.duration > 0) {
          setTimeout(() => {
            get().removeToast(id);
          }, newToast.duration);
        }

        return id;
      },

      removeToast: (id) => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }));
      },

      clearToasts: () => {
        set({ toasts: [] });
      },

      // Convenience methods for toasts
      showSuccess: (message, duration) => {
        return get().addToast({ type: 'success', message, duration });
      },

      showError: (message, duration) => {
        return get().addToast({ type: 'error', message, duration });
      },

      showWarning: (message, duration) => {
        return get().addToast({ type: 'warning', message, duration });
      },

      showInfo: (message, duration) => {
        return get().addToast({ type: 'info', message, duration });
      },

      // Actions - Sidebar
      toggleSidebar: () => {
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            open: !state.sidebar.open,
          },
        }));
      },

      setSidebarOpen: (open) => {
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            open,
          },
        }));
      },

      setSidebarMobile: (mobile) => {
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            mobile,
          },
        }));
      },

      // Actions - Loading
      setGlobalLoading: (loading, message = null) => {
        set({
          globalLoading: loading,
          loadingMessage: message,
        });
      },

      // Actions - Drawer
      openDrawer: (content, position = 'right') => {
        set({
          drawer: {
            open: true,
            content,
            position,
          },
        });
      },

      closeDrawer: () => {
        set({
          drawer: {
            open: false,
            content: null,
            position: 'right',
          },
        });
      },

      // Reset
      reset: () => {
        set({
          theme: 'system',
          effectiveTheme: 'light',
          modals: {
            login: false,
            signup: false,
            transaction: false,
            payment: false,
            dispute: false,
            withdrawal: false,
            settings: false,
            profile: false,
            kyc: false,
            deleteAccount: false,
            generic: {
              open: false,
              component: null,
              props: {},
            },
          },
          toasts: [],
          sidebar: {
            open: false,
            mobile: false,
          },
          globalLoading: false,
          loadingMessage: null,
          drawer: {
            open: false,
            content: null,
            position: 'right',
          },
        });
      },
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({
        // Don't persist theme - app is always dark by default
        sidebar: state.sidebar,
        // Don't persist modals, toasts, loading - these are ephemeral
      }),
    }
  )
);

export default useUIStore;
